import { IconButton } from '@civilgrid/ui/components';
import type { Table } from '@tanstack/react-table';

type OnPageChangeProps = {
  onClick?: () => void;
  disabled?: boolean;
};

export interface PaginationProps {
  table: Table<any>;
  rowCount?: number;
  firstPage?: OnPageChangeProps;
  previousPage?: OnPageChangeProps;
  nextPage?: OnPageChangeProps;
  lastPage?: OnPageChangeProps;
  'data-testid'?: string;
}

export default function ({
  table,
  rowCount,
  firstPage,
  previousPage,
  nextPage,
  lastPage,
  'data-testid': dataTestId,
}: PaginationProps) {
  const { pageIndex, pageSize } = table.getState().pagination;

  const totalRows = rowCount !== undefined ? rowCount : table.getRowCount();
  const startRow = pageIndex * pageSize + 1;
  const endRow = Math.min(startRow + pageSize - 1, totalRows);

  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  return (
    <div className="flex items-center space-x-2" data-testid={dataTestId}>
      <IconButton
        icon="doubleArrowLeft"
        variant="secondary"
        size="icon"
        disabled={firstPage?.disabled !== undefined ? firstPage.disabled : !table.getCanPreviousPage()}
        onClick={() => {
          if (firstPage?.onClick) {
            firstPage.onClick();
          } else {
            table.firstPage();
          }
        }}
        aria-label="First page"
        data-testid={testId('first-page')}
      />
      <IconButton
        icon="chevronLeft"
        variant="secondary"
        size="icon"
        disabled={previousPage?.disabled !== undefined ? previousPage.disabled : !table.getCanPreviousPage()}
        onClick={() => {
          if (previousPage?.onClick) {
            previousPage.onClick();
          } else {
            table.previousPage();
          }
        }}
        aria-label="Previous page"
        data-testid={testId('previous-page')}
      />
      <div className="text-xs" data-testid={testId('range')}>
        {startRow} - {endRow} of {totalRows}
      </div>
      <IconButton
        icon="chevronRight"
        variant="secondary"
        size="icon"
        disabled={nextPage?.disabled !== undefined ? nextPage.disabled : !table.getCanNextPage()}
        onClick={() => {
          if (nextPage?.onClick) {
            nextPage.onClick();
          } else {
            table.nextPage();
          }
        }}
        aria-label="Next page"
        data-testid={testId('next-page')}
      />
      <IconButton
        icon="doubleArrowRight"
        variant="secondary"
        size="icon"
        disabled={lastPage?.disabled !== undefined ? lastPage.disabled : !table.getCanNextPage()}
        onClick={() => {
          if (lastPage?.onClick) {
            lastPage.onClick();
          } else {
            table.lastPage();
          }
        }}
        aria-label="Last page"
        data-testid={testId('last-page')}
      />
    </div>
  );
}
